<template>
  <button v-if="!open && showFab" class="matcher-fab" @click="open = true">
    <span class="fab-icon-wrap">
      <q-icon name="auto_awesome" size="20px" class="fab-icon" />
    </span>
    <span class="fab-text">{{ $t('program_matcher.fab_label') }}</span>
    <q-icon name="arrow_forward" size="16px" class="fab-arrow" />
  </button>

  <q-dialog v-model="open" persistent class="matcher-dialog">
    <q-card class="matcher-card" :class="isGreek ? 'font-manrope' : 'font-poppins'">
      <div class="matcher-header">
        <div class="row items-center no-wrap">
          <div class="header-icon-wrap q-mr-sm q-mr-sm-md">
            <q-icon name="auto_awesome" size="28px" color="primary" />
          </div>
          <div class="col">
            <div class="matcher-title text-white text-weight-bold">
              AthenAI <span class="aithena-title-sub">· {{ $t('program_matcher.title') }}</span>
            </div>
            <div v-if="!done" class="text-caption text-white q-mt-xs" style="opacity: 0.65">
              {{ $t('program_matcher.step_counter', { current: step + 1, total: totalSteps }) }}
            </div>
          </div>
          <q-btn icon="close" flat round dense color="white" v-close-popup class="q-ml-xs" />
        </div>

        <div v-if="!done && !resumed && selectionCrumbs.length > 0" class="step-crumbs q-mt-sm">
          <template v-for="(crumb, i) in selectionCrumbs" :key="i">
            <span v-if="i > 0" class="step-crumb-sep">›</span>
            <span class="step-crumb">{{ crumb }}</span>
          </template>
        </div>

        <div v-if="!done && !resumed" class="q-mt-md">
          <div class="step-progress-track">
            <div
              class="step-progress-fill"
              :style="{ width: `${((step + 1) / totalSteps) * 100}%` }"
            />
          </div>
          <div class="row q-mt-sm q-gutter-xs">
            <div
              v-for="i in totalSteps"
              :key="i"
              class="step-dot"
              :class="{ active: i === step + 1, done: i < step + 1 }"
            />
          </div>
        </div>
      </div>

      <q-card-section v-if="!done && !resumed" class="step-section">
        <template v-if="step === 0">
          <p class="step-q">{{ $t('program_matcher.steps.type.question') }}</p>
          <div class="chip-row">
            <div
              v-for="o in typeOpts"
              :key="o.value"
              class="option-chip"
              :class="{ selected: form.programme_type === o.value }"
              @click="form.programme_type = o.value"
            >
              {{ o.label }}
            </div>
          </div>
        </template>

        <template v-if="step === 1">
          <p class="step-q">
            {{ $t('program_matcher.steps.topics.question') }}
            <span class="step-hint">{{ $t('program_matcher.steps.topics.hint') }}</span>
          </p>
          <div class="chip-row">
            <div
              v-for="topic in topicOpts"
              :key="topic.value"
              class="option-chip"
              :class="{ selected: form.topics.includes(topic.value) }"
              @click="toggleTopic(topic.value)"
            >
              {{ topic.label }}
            </div>
          </div>
        </template>

        <template v-if="step === 2">
          <p class="step-q">{{ $t('program_matcher.steps.mode.question') }}</p>
          <div class="chip-row">
            <div
              v-for="o in modeOpts"
              :key="o.value"
              class="option-chip"
              :class="{ selected: form.study_mode === o.value }"
              @click="form.study_mode = o.value"
            >
              {{ o.label }}
            </div>
          </div>
        </template>

        <template v-if="step === 3">
          <p class="step-q">{{ $t('program_matcher.steps.language.question') }}</p>
          <div class="chip-row">
            <div
              v-for="o in languageOpts"
              :key="o.value"
              class="option-chip"
              :class="{ selected: form.language === o.value }"
              @click="form.language = o.value"
            >
              {{ o.label }}
            </div>
          </div>
        </template>

        <template v-if="step === 4">
          <p class="step-q">{{ $t('program_matcher.steps.tuition.question') }}</p>
          <div class="chip-row">
            <div
              v-for="o in tuitionOpts"
              :key="o.value"
              class="option-chip"
              :class="{ selected: form.tuition === o.value }"
              @click="form.tuition = o.value"
            >
              {{ o.label }}
            </div>
          </div>
          <!-- Temporarily hidden: not enough programmes with scholarship/open-applications data
          <div class="q-mt-lg toggle-row">
            <q-toggle
              v-model="form.scholarship_required"
              :label="$t('program_matcher.steps.tuition.scholarship')"
              color="primary"
            />
            <q-toggle
              v-model="form.open_for_applications"
              :label="$t('program_matcher.steps.tuition.open_applications')"
              color="primary"
            />
          </div>
          -->
        </template>

        <template v-if="step === 5">
          <div class="free-text-boost q-mb-md">
            <q-icon name="bolt" size="16px" class="free-text-boost-icon" />
            <span>{{ $t('program_matcher.steps.free_text.boost') }}</span>
          </div>

          <p class="step-q q-mt-md q-mb-lg">
            {{ $t('program_matcher.steps.free_text.question') }}
            <span class="step-hint"> {{ $t('program_matcher.steps.free_text.hint') }}</span>
          </p>

          <div class="suggestion-chips q-mb-sm">
            <div
              v-for="suggestion in freeTextSuggestions"
              :key="suggestion"
              class="suggestion-chip"
              @click="applySuggestion(suggestion)"
            >
              <q-icon name="add" size="11px" class="q-mr-xs" />{{ suggestion }}
            </div>
          </div>

          <q-input
            v-model="form.free_text"
            outlined
            autogrow
            color="primary"
            :placeholder="$t('program_matcher.steps.free_text.placeholder')"
            maxlength="600"
            counter
            class="q-mt-sm"
            input-style="font-size: 15px"
          />

          <div class="free-text-tip q-mt-sm">
            <q-icon name="lightbulb_outline" size="13px" class="q-mr-xs free-text-tip-icon" style="color: #f59e0b" />
            {{ $t('program_matcher.steps.free_text.tip') }}
          </div>
        </template>
      </q-card-section>

      <q-card-actions v-if="!done && !resumed" class="nav-actions" :class="{ 'nav-actions--last': step === totalSteps - 1 }">
        <q-btn
          v-if="step > 0"
          outline
          color="primary"
          :label="$t('program_matcher.nav.back')"
          icon="arrow_back"
          @click="step--"
          no-caps
          rounded
          class="nav-btn"
        />
        <q-space />
        <q-btn
          :label="
            step < totalSteps - 1
              ? $t('program_matcher.nav.next')
              : $t('program_matcher.nav.find_matches')
          "
          :icon-right="step < totalSteps - 1 ? 'arrow_forward' : 'auto_awesome'"
          color="primary"
          unelevated
          rounded
          no-caps
          :disable="!stepValid"
          @click="advance"
          class="nav-btn nav-btn--primary"
        />
      </q-card-actions>

      <template v-if="resumed">
        <div class="results-header">
          <q-icon name="history" color="primary" size="20px" style="flex-shrink: 0" />
          <span class="text-subtitle2 text-weight-bold text-primary q-ml-sm">{{
            $t('program_matcher.resume.title')
          }}</span>
        </div>
        <q-card-section class="step-section">
          <p class="step-q">
            {{ $t('program_matcher.resume.subtitle') }}
            <span class="step-hint">{{ sessionAge }}</span>
          </p>
          <div class="chip-row q-mb-sm">
            <div v-for="chip in sessionSummary" :key="chip" class="option-chip option-chip--static">
              {{ chip }}
            </div>
          </div>
          <p class="text-caption text-grey-6">
            {{
              (pendingSession?.results?.length ?? 0) === 1
                ? $t('program_matcher.resume.matched_singular')
                : $t('program_matcher.resume.matched_plural', {
                    count: pendingSession?.results?.length ?? 0,
                  })
            }}
          </p>
        </q-card-section>
        <q-card-actions class="nav-actions">
          <q-btn
            outline
            rounded
            color="grey-6"
            :label="$t('program_matcher.resume.new_search')"
            icon="refresh"
            no-caps
            @click="reset"
            class="nav-btn"
          />
          <q-space />
          <q-btn
            unelevated
            rounded
            color="primary"
            :label="$t('program_matcher.resume.see_results')"
            icon-right="arrow_forward"
            no-caps
            @click="restoreSession"
            class="nav-btn nav-btn--primary"
          />
        </q-card-actions>
      </template>

      <template v-if="done && !resumed">
        <template v-if="refining">
          <div class="results-header">
            <q-btn
              flat
              round
              dense
              icon="arrow_back"
              color="primary"
              size="sm"
              @click="refining = false"
            />
            <span class="text-subtitle2 text-weight-bold text-primary q-ml-sm">{{
              $t('program_matcher.refine.title')
            }}</span>
          </div>

          <q-card-section class="step-section">
            <p class="step-q">
              {{ $t('program_matcher.refine.career_question') }}
              <span class="step-hint"> {{ $t('program_matcher.refine.career_hint') }}</span>
            </p>
            <q-input
              v-model="refineForm.career_goals"
              outlined
              autogrow
              color="primary"
              :placeholder="$t('program_matcher.refine.career_placeholder')"
              maxlength="800"
              counter
              class="q-mb-lg"
            />

            <p class="step-q">
              {{ $t('program_matcher.refine.background_question') }}
              <span class="step-hint"> {{ $t('program_matcher.refine.background_hint') }}</span>
            </p>
            <q-input
              v-model="refineForm.academic_background"
              outlined
              autogrow
              color="primary"
              :placeholder="$t('program_matcher.refine.background_placeholder')"
              maxlength="800"
              counter
              class="q-mb-lg"
            />

            <p class="step-q">
              {{ $t('program_matcher.refine.topics_question') }}
              <span class="step-hint"> {{ $t('program_matcher.refine.topics_hint') }}</span>
            </p>
            <q-input
              v-model="refineForm.extra"
              outlined
              autogrow
              color="primary"
              :placeholder="$t('program_matcher.refine.topics_placeholder')"
              maxlength="600"
              counter
            />
          </q-card-section>

          <div v-if="refineError" class="refine-error">
            <q-icon name="error_outline" size="16px" class="q-mr-xs" />
            {{ $t('program_matcher.refine.error') }}
          </div>

          <q-card-actions class="nav-actions nav-actions--refine">
            <q-btn outline rounded color="primary" :label="$t('program_matcher.refine.back')" icon="arrow_back" no-caps @click="refining = false" class="nav-btn" />
            <q-space />
            <q-btn
              unelevated
              rounded
              color="primary"
              no-caps
              :label="$t('program_matcher.refine.submit')"
              icon-right="auto_awesome"
              :disable="!refineCanSubmit"
              :loading="loading"
              @click="submitRefine"
              class="nav-btn nav-btn--primary"
            />
          </q-card-actions>
        </template>

        <template v-else>
          <div class="results-header">
            <template v-if="loading">
              <q-spinner-dots color="primary" size="26px" class="q-mr-sm" />
              <span class="text-subtitle1 text-grey-7">{{
                $t('program_matcher.results.searching')
              }}</span>
            </template>
            <template v-else>
              <div class="results-title-row">
                <q-icon name="check_circle" color="positive" size="22px" style="flex-shrink: 0" />
                <span class="text-subtitle1 text-weight-bold text-primary q-ml-sm">
                  {{
                    results.length === 1
                      ? $t('program_matcher.results.matched_singular')
                      : $t('program_matcher.results.matched_plural', { count: results.length })
                  }}
                </span>
              </div>
              <div class="results-header-btns">
                <q-btn
                  outline
                  rounded
                  color="primary"
                  :label="$t('program_matcher.results.not_dream_match')"
                  icon="auto_awesome"
                  no-caps
                  class="results-action-btn refine-cta"
                  @click="refining = true"
                />
                <q-btn
                  unelevated
                  rounded
                  color="primary"
                  :label="$t('program_matcher.results.start_over')"
                  icon="refresh"
                  no-caps
                  class="results-action-btn"
                  @click="reset"
                />
              </div>
            </template>
          </div>

          <div v-if="!loading && matchError" class="empty-state">
            <q-icon name="error_outline" size="56px" color="negative" />
            <div class="text-body1 text-weight-medium text-grey-7 q-mt-sm">{{ matchError }}</div>
            <q-btn flat rounded color="primary" label="Start over" icon="refresh" no-caps class="q-mt-sm" @click="reset" />
          </div>

          <div v-if="!loading && !matchError && results.length === 0 && fallbackResults.length === 0" class="empty-state">
            <q-icon name="search_off" size="56px" color="grey-4" />
            <div class="text-body1 text-weight-medium text-grey-7 q-mt-sm">
              {{ $t('program_matcher.results.no_matches_title') }}
            </div>
            <div v-if="selectionCrumbs.length > 0" class="empty-state-crumbs q-mt-xs">
              <template v-for="(crumb, i) in selectionCrumbs" :key="i">
                <span v-if="i > 0" class="empty-crumb-sep">›</span>
                <span class="empty-crumb">{{ crumb }}</span>
              </template>
            </div>
            <div class="text-caption text-grey-5">
              {{ $t('program_matcher.results.no_matches_desc') }}
            </div>
          </div>

          <div
            v-if="!loading && results.length === 0 && fallbackResults.length > 0"
            class="result-list"
          >
            <p v-if="hint" class="fallback-hint-text">{{ hint }}</p>
            <div v-for="(prog, index) in fallbackResults" :key="prog.programme_id" class="result-card">
              <div class="result-inner">
                <div class="result-logo-wrap">
                  <q-img
                    v-if="prog.university_image_url"
                    :src="prog.university_image_url"
                    class="result-logo"
                    fit="contain"
                  />
                  <div v-else class="result-logo result-logo-placeholder">
                    <q-icon name="school" size="32px" color="primary" />
                  </div>
                </div>
                <div class="result-body">
                  <div class="result-top-row">
                    <p class="result-title">
                      {{ isGreek && prog.name_gr ? prog.name_gr : prog.name_en }}
                    </p>
                    <div class="score-pill" :class="getMatchClass(prog.score)">
                      {{ getMatchLabel(prog.score) }}
                    </div>
                  </div>
                  <p class="result-meta">
                    {{
                      isGreek && prog.university_name_gr
                        ? prog.university_name_gr
                        : prog.university_name
                    }}<span v-if="prog.city_en">
                      · {{ isGreek && prog.city_gr ? prog.city_gr : prog.city_en }}</span
                    >
                  </p>
                  <div class="badge-row">
                    <q-badge outline color="primary" class="badge-item">{{ translateType(prog.programme_type) }}</q-badge>
                    <img v-if="prog.has_english" :src="getFlagUrl('English')" class="lang-flag" title="English" />
                    <img v-if="prog.has_greek" :src="getFlagUrl('Greek')" class="lang-flag" title="Greek" />
                    <q-badge :color="prog.has_tuition_fees ? 'grey-4' : 'positive'" :text-color="prog.has_tuition_fees ? 'grey-9' : 'white'" class="badge-item">
                      {{ prog.has_tuition_fees ? `€${prog.tuition_fees_from?.toLocaleString()}` : $t('program_matcher.results.badges.free') }}
                    </q-badge>
                    <q-badge
                      v-for="m in prog.study_modes"
                      :key="m"
                      color="secondary"
                      text-color="primary"
                      class="badge-item"
                      >{{ translateMode(m) }}</q-badge
                    >
                    <q-badge
                      v-if="prog.apply_status"
                      color="positive"
                      text-color="white"
                      class="badge-item"
                      >{{ $t('program_matcher.results.badges.open') }}</q-badge
                    >
                  </div>
                  <div class="result-reasoning">
                    <span class="aithena-badge"
                      ><q-icon name="auto_awesome" size="11px" />AthenAI</span
                    >
                    {{ prog.reasoning || getFallbackReasoning(prog) }}
                  </div>
                  <div class="card-actions-row">
                    <q-btn
                      unelevated
                      rounded
                      size="sm"
                      color="primary"
                      :label="$t('program_matcher.results.view_program')"
                      icon-right="open_in_new"
                      no-caps
                      class="result-view-btn"
                      :href="getProgramPageUrl(prog)"
                      target="_blank"
                      @click="trackClick(prog, index)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="result-list">
            <div v-for="(prog, index) in results" :key="prog.programme_id" class="result-card">
              <div class="result-inner">
                <div class="result-logo-wrap">
                  <q-img
                    v-if="prog.university_image_url"
                    :src="prog.university_image_url"
                    class="result-logo"
                    fit="contain"
                  />
                  <div v-else class="result-logo result-logo-placeholder">
                    <q-icon name="school" size="32px" color="primary" />
                  </div>
                </div>
                <div class="result-body">
                  <div class="result-top-row">
                    <p class="result-title">
                      {{ isGreek && prog.name_gr ? prog.name_gr : prog.name_en }}
                    </p>
                    <div class="score-pill" :class="getMatchClass(prog.score)">
                      {{ getMatchLabel(prog.score) }}
                    </div>
                  </div>
                  <p class="result-meta">
                    {{
                      isGreek && prog.university_name_gr
                        ? prog.university_name_gr
                        : prog.university_name
                    }}<span v-if="prog.city_en">
                      · {{ isGreek && prog.city_gr ? prog.city_gr : prog.city_en }}</span
                    >
                  </p>
                  <div class="badge-row">
                    <q-badge outline color="primary" class="badge-item">{{ translateType(prog.programme_type) }}</q-badge>
                    <img v-if="prog.has_english" :src="getFlagUrl('English')" class="lang-flag" title="English" />
                    <img v-if="prog.has_greek" :src="getFlagUrl('Greek')" class="lang-flag" title="Greek" />
                    <q-badge :color="prog.has_tuition_fees ? 'grey-4' : 'positive'" :text-color="prog.has_tuition_fees ? 'grey-9' : 'white'" class="badge-item">
                      {{ prog.has_tuition_fees ? `€${prog.tuition_fees_from?.toLocaleString()}` : $t('program_matcher.results.badges.free') }}
                    </q-badge>
                    <q-badge
                      v-for="m in prog.study_modes"
                      :key="m"
                      color="secondary"
                      text-color="primary"
                      class="badge-item"
                      >{{ translateMode(m) }}</q-badge
                    >
                    <q-badge
                      v-if="prog.apply_status"
                      color="positive"
                      text-color="white"
                      class="badge-item"
                      >{{ $t('program_matcher.results.badges.open') }}</q-badge
                    >
                  </div>
                  <div class="result-reasoning">
                    <span class="aithena-badge"
                      ><q-icon name="auto_awesome" size="11px" />AthenAI</span
                    >
                    {{ prog.reasoning || getFallbackReasoning(prog) }}
                  </div>
                  <div class="card-actions-row">
                    <q-btn
                      unelevated
                      rounded
                      size="sm"
                      color="primary"
                      :label="$t('program_matcher.results.view_program')"
                      icon-right="open_in_new"
                      no-caps
                      class="result-view-btn"
                      :href="getProgramPageUrl(prog)"
                      target="_blank"
                      @click="trackClick(prog, index)"
                    />
                    <div class="feedback-row">
                      <button
                        class="feedback-btn"
                        :class="{
                          'feedback-btn--active feedback-btn--like':
                            feedback[prog.programme_id] === 'like',
                        }"
                        @click="sendFeedback(prog.programme_id, 'like')"
                        title="Good match"
                      >
                        <q-icon name="thumb_up" size="13px" />
                      </button>
                      <button
                        class="feedback-btn"
                        :class="{
                          'feedback-btn--active feedback-btn--dislike':
                            feedback[prog.programme_id] === 'dislike',
                        }"
                        @click="sendFeedback(prog.programme_id, 'dislike')"
                        title="Not a good match"
                      >
                        <q-icon name="thumb_down" size="13px" />
                      </button>
                    </div>
                    <button
                      class="compare-toggle"
                      :class="{ 'compare-toggle--active': compareList.includes(prog.programme_id) }"
                      :disabled="
                        compareList.length >= 2 && !compareList.includes(prog.programme_id)
                      "
                      @click="toggleCompare(prog.programme_id)"
                    >
                      {{
                        compareList.includes(prog.programme_id)
                          ? $t('program_matcher.results.selected')
                          : $t('program_matcher.results.compare_btn')
                      }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="compareList.length === 2" class="compare-bar">
              <q-icon name="compare" size="16px" class="q-mr-sm" />
              <span>{{ $t('program_matcher.results.compare_selected') }}</span>
              <q-space />
              <q-btn
                unelevated
                rounded
                size="sm"
                color="primary"
                :label="$t('program_matcher.results.compare_btn')"
                no-caps
                :loading="compareLoading"
                @click="runCompare"
                class="q-mr-sm"
              />
              <q-btn
                flat
                round
                dense
                icon="close"
                size="sm"
                color="grey-7"
                @click="compareList = []"
              />
            </div>
          </div>
        </template>
      </template>
    </q-card>
  </q-dialog>

  <div ref="turnstileContainer" style="display: none" />

  <q-dialog v-model="compareDialog" class="compare-dialog">
    <q-card class="compare-dialog-card" :class="isGreek ? 'font-manrope' : 'font-poppins'">
      <div class="compare-dialog-header">
        <q-icon name="auto_awesome" size="15px" color="primary" />
        <span class="text-weight-bold text-primary" style="font-size: 16px">{{
          $t('program_matcher.compare.title')
        }}</span>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup size="sm" color="grey-7" />
      </div>

      <div v-if="compareLoading" class="text-center q-py-xl">
        <q-spinner-dots color="primary" size="32px" />
        <div class="text-caption text-grey-6 q-mt-sm">
          {{ $t('program_matcher.compare.comparing') }}
        </div>
      </div>

      <template v-else-if="parsedCompare">
        <div class="cmp-wrapper">
          <div class="cmp-headers">
            <div class="cmp-col-header">
              {{
                compareProgs[0]
                  ? isGreek && compareProgs[0].name_gr
                    ? compareProgs[0].name_gr
                    : compareProgs[0].name_en
                  : ''
              }}
            </div>
            <div class="cmp-col-header cmp-col-header--b">
              {{
                compareProgs[1]
                  ? isGreek && compareProgs[1].name_gr
                    ? compareProgs[1].name_gr
                    : compareProgs[1].name_en
                  : ''
              }}
            </div>
          </div>

          <div class="cmp-section">
            <div class="cmp-section-label">
              <q-icon name="menu_book" size="14px" />
              {{ $t('program_matcher.compare.focus') }}
            </div>
            <div class="cmp-section-cols">
              <div class="cmp-cell">{{ parsedCompare.focus_a }}</div>
              <div class="cmp-cell cmp-cell--b">{{ parsedCompare.focus_b }}</div>
            </div>
          </div>

          <div class="cmp-section">
            <div class="cmp-section-label">
              <q-icon name="person_outline" size="14px" />
              {{ $t('program_matcher.compare.best_for') }}
            </div>
            <div class="cmp-section-cols">
              <div class="cmp-cell">{{ parsedCompare.for_a }}</div>
              <div class="cmp-cell cmp-cell--b">{{ parsedCompare.for_b }}</div>
            </div>
          </div>
        </div>

        <div class="cmp-verdict">
          <q-icon
            name="lightbulb_outline"
            size="16px"
            color="amber-8"
            style="flex-shrink: 0; margin-top: 1px"
          />
          <span>{{ parsedCompare.verdict }}</span>
        </div>
      </template>

      <div v-else-if="compareResult" class="q-pa-md">
        <p class="text-body2" style="line-height: 1.65">{{ compareResult }}</p>
      </div>

      <div v-else-if="!compareLoading" class="text-center text-grey-5 q-py-lg text-caption">
        {{ $t('program_matcher.compare.unavailable') }}
      </div>

      <q-card-actions align="right" class="q-px-md q-pb-md q-pt-xs">
        <q-btn
          flat
          rounded
          color="primary"
          :label="$t('program_matcher.compare.close')"
          no-caps
          v-close-popup
          @click="compareList = []"
          size="sm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { useMatcherState } from 'src/composables/useMatcherState'
import { useLanguageFlags } from 'src/composables/useLanguageFlags'
import { useAppUrls } from 'src/composables/useAppUrls'
import { useApplicantStore } from 'src/stores/applicant-store'
import { apiTypeToRouteType } from 'src/utils/programmeTypeMapper'
import { slugify } from 'src/utils/slugify'
import type { ProgrammeApiType } from 'src/types/Programme'

interface ProgramMatch {
  programme_id: number
  programme_type: string
  name_en: string
  name_gr: string | null
  university_name: string
  university_name_gr: string | null
  university_image_url: string | null
  department_name: string | null
  city_en: string | null
  city_gr: string | null
  website: string | null
  ects: number | null
  has_tuition_fees: boolean
  tuition_fees_from: number | null
  topics: string[]
  study_modes: string[]
  has_english: boolean
  has_greek: boolean
  apply_status: boolean
  score: number
  reasoning: string | null
  scraped_description: string | null
  scraped_description_gr: string | null
}

interface MatchResponse {
  results: ProgramMatch[]
  total_found: number
  cached: boolean
  hint?: string
  fallback_results?: ProgramMatch[]
  session_id?: string
}

interface TrendingProgramme {
  programme_id: number
  name_en: string
  university_name: string
  appearances: number
}

interface MatchForm {
  programme_type: string
  topics: string[]
  study_mode: string
  language: string
  tuition: string
  scholarship_required: boolean
  open_for_applications: boolean
  free_text: string
}

interface MatcherSession {
  form: MatchForm
  results: ProgramMatch[]
  hint: string | null
  fallbackResults: ProgramMatch[]
  feedback: Record<number, 'like' | 'dislike'>
  trending: TrendingProgramme[]
  savedAt: string
}

const { t, locale } = useI18n()
const { open } = useMatcherState()
const { getFlagUrl } = useLanguageFlags()
const isGreek = computed(() => locale.value.startsWith('el'))
const { getProgramUrl } = useAppUrls()
const applicantStore = useApplicantStore()

const getProgramPageUrl = (prog: ProgramMatch): string => {
  const routeType =
    apiTypeToRouteType[prog.programme_type as ProgrammeApiType] ?? prog.programme_type.toLowerCase()
  const slug = slugify(prog.name_en)
  return getProgramUrl(prog.programme_id, routeType, slug)
}

const showFab = ref(true)

const API_BASE = (
  (import.meta.env.VITE_MATCHER_BASE_URL as string | undefined) ?? '/matcher-api/api/v1'
).replace(/\/$/, '')
const MATCHER_URL = `${API_BASE}/match`
const FEEDBACK_URL = `${API_BASE}/feedback`
const CLICK_URL = `${API_BASE}/click`
const COMPARE_URL = `${API_BASE}/compare`
const TRENDING_URL = `${API_BASE}/trending`

// Cloudflare Turnstile (invisible mode)
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      execute: (id: string) => void
      reset: (id: string) => void
    }
  }
}

const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? ''
const turnstileContainer = ref<HTMLElement | null>(null)
let turnstileWidgetId: string | null = null
let resolveToken: ((token: string) => void) | null = null

function initTurnstileWidget() {
  if (!turnstileContainer.value || !TURNSTILE_SITE_KEY || !window.turnstile) return
  turnstileWidgetId = window.turnstile.render(turnstileContainer.value, {
    sitekey: TURNSTILE_SITE_KEY,
    size: 'invisible',
    execution: 'execute',
    callback: (token: string) => {
      resolveToken?.(token)
      resolveToken = null
    },
  })
}

onMounted(() => {
  if (!TURNSTILE_SITE_KEY) return
  if (document.querySelector('script[data-cf-turnstile]')) {
    initTurnstileWidget()
    return
  }
  const script = document.createElement('script')
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
  script.dataset.cfTurnstile = 'true'
  script.async = true
  script.defer = true
  script.onload = () => initTurnstileWidget()
  document.head.appendChild(script)
})

async function getTurnstileToken(): Promise<string> {
  if (!TURNSTILE_SITE_KEY || !window.turnstile || turnstileWidgetId === null) return ''
  window.turnstile.reset(turnstileWidgetId)
  return new Promise<string>((resolve) => {
    resolveToken = resolve
    window.turnstile!.execute(turnstileWidgetId!)
  })
}

const step = ref(0)
const done = ref(false)
const loading = ref(false)
const results = ref<ProgramMatch[]>([])
const hint = ref<string | null>(null)
const fallbackResults = ref<ProgramMatch[]>([])
const feedback = reactive<Record<number, 'like' | 'dislike'>>({})
const compareList = ref<number[]>([])
const compareResult = ref<string | null>(null)
const compareLoading = ref(false)
const compareDialog = ref(false)
const compareProgs = ref<ProgramMatch[]>([])

interface CompareStructured {
  focus_a: string
  focus_b: string
  for_a: string
  for_b: string
  verdict: string
}

const parsedCompare = computed((): CompareStructured | null => {
  if (!compareResult.value) return null
  try {
    const data = JSON.parse(compareResult.value)
    if (data.focus_a && data.focus_b && data.for_a && data.for_b && data.verdict)
      return data as CompareStructured
    return null
  } catch {
    return null
  }
})
const sessionId = ref<string | null>(null)
const trending = ref<TrendingProgramme[]>([])
const resumed = ref(false)
const sessionAge = ref('')
const pendingSession = ref<MatcherSession | null>(null)
const refining = ref(false)
const refineError = ref(false)
const matchError = ref<string | null>(null)
const refineForm = reactive({ career_goals: '', academic_background: '', extra: '' })
const refineCanSubmit = computed(
  () =>
    refineForm.career_goals.trim().length > 2 ||
    refineForm.academic_background.trim().length > 2 ||
    refineForm.extra.trim().length > 2
)
const totalSteps = 6

const form = reactive<MatchForm>({
  programme_type: 'any',
  topics: [],
  study_mode: 'any',
  language: 'any',
  tuition: 'any',
  scholarship_required: false,
  open_for_applications: false,
  free_text: '',
})

const typeOpts = computed(() => [
  { label: t('program_matcher.steps.type.options.master'), value: 'Master' },
  { label: t('program_matcher.steps.type.options.bachelor'), value: 'Bachelor' },
  { label: t('program_matcher.steps.type.options.any'), value: 'any' },
])

const topicOpts = computed(() => [
  { label: t('program_matcher.steps.topics.options.technology'), value: 'Technology' },
  { label: t('program_matcher.steps.topics.options.science'), value: 'Science and Engineering' },
  { label: t('program_matcher.steps.topics.options.health'), value: 'Health' },
  { label: t('program_matcher.steps.topics.options.economics'), value: 'Economics Management' },
  { label: t('program_matcher.steps.topics.options.humanities'), value: 'Humanities' },
  {
    label: t('program_matcher.steps.topics.options.environmental'),
    value: 'Environmental Sciences',
  },
  { label: t('program_matcher.steps.topics.options.social'), value: 'Social Sciences' },
  { label: t('program_matcher.steps.topics.options.law'), value: 'Law Sciences' },
  { label: t('program_matcher.steps.topics.options.agriculture'), value: 'Agriculture' },
  { label: t('program_matcher.steps.topics.options.politics'), value: 'Politics' },
  { label: t('program_matcher.steps.topics.options.arts'), value: 'Arts' },
  { label: t('program_matcher.steps.topics.options.education'), value: 'Education Sciences' },
  { label: t('program_matcher.steps.topics.options.tourism'), value: 'Tourism Studies' },
  { label: t('program_matcher.steps.topics.options.sports'), value: 'Sports' },
])

const modeOpts = computed(() => [
  { label: t('program_matcher.steps.mode.options.in_person'), value: 'In Person' },
  { label: t('program_matcher.steps.mode.options.online'), value: 'Remotely' },
  { label: t('program_matcher.steps.mode.options.any'), value: 'any' },
])

const languageOpts = computed(() => [
  { label: t('program_matcher.steps.language.options.english'), value: 'English' },
  { label: t('program_matcher.steps.language.options.greek'), value: 'Greek' },
  { label: t('program_matcher.steps.language.options.any'), value: 'any' },
])

const tuitionOpts = computed(() => [
  { label: t('program_matcher.steps.tuition.options.free'), value: 'free' },
  { label: t('program_matcher.steps.tuition.options.low'), value: 'low' },
  { label: t('program_matcher.steps.tuition.options.mid'), value: 'mid' },
  { label: t('program_matcher.steps.tuition.options.any'), value: 'any' },
])

const freeTextSuggestions = computed(() => [
  t('program_matcher.steps.free_text.suggestions.career'),
  t('program_matcher.steps.free_text.suggestions.background'),
  t('program_matcher.steps.free_text.suggestions.research'),
  t('program_matcher.steps.free_text.suggestions.location'),
])

const applySuggestion = (text: string) => {
  if (form.free_text.trim()) {
    form.free_text = form.free_text.trim() + '. ' + text
  } else {
    form.free_text = text
  }
}

const stepValid = computed(() => {
  if (step.value === 1) return form.topics.length > 0
  return true
})

const toggleTopic = (t: string) => {
  const i = form.topics.indexOf(t)
  if (i === -1) form.topics.push(t)
  else form.topics.splice(i, 1)
}

const advance = async () => {
  if (step.value < totalSteps - 1) {
    step.value++
    return
  }
  done.value = true
  loading.value = true
  matchError.value = null
  try {
    const cf_turnstile_response = await getTurnstileToken()
    const payload: Record<string, unknown> = {
      programme_type: form.programme_type,
      study_mode: form.study_mode,
      tuition: form.tuition,
      topics: form.topics,
      language: form.language,
      scholarship_required: form.scholarship_required,
      open_for_applications: form.open_for_applications,
      locale: locale.value,
      cf_turnstile_response,
    }
    if (form.free_text.trim()) payload.free_text = form.free_text.trim()
    const nationality = applicantStore.selectedApplicant?.nationalities[0]
    if (nationality) payload.country_of_origin = nationality

    const { data } = await axios.post<MatchResponse>(MATCHER_URL, payload)
    results.value = data.results
    hint.value = data.hint ?? null
    fallbackResults.value = data.fallback_results ?? []
    sessionId.value = data.session_id ?? null
    if (data.results.length > 0) void fetchTrending()
    saveSession()
  } catch (e: unknown) {
    console.error('Matcher error:', e)
    const status = (e as { response?: { status?: number } })?.response?.status
    matchError.value =
      status === 429
        ? 'Too many requests. Please wait a moment and try again.'
        : 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}

const submitRefine = async () => {
  refineError.value = false
  loading.value = true
  try {
    const cf_turnstile_response = await getTurnstileToken()
    const richParts: string[] = []
    if (refineForm.career_goals.trim())
      richParts.push(`Career goals: ${refineForm.career_goals.trim()}`)
    if (refineForm.academic_background.trim())
      richParts.push(`Academic background: ${refineForm.academic_background.trim()}`)
    if (refineForm.extra.trim()) richParts.push(`Specific interests: ${refineForm.extra.trim()}`)
    if (form.free_text.trim()) richParts.push(form.free_text.trim())

    const payload: Record<string, unknown> = {
      programme_type: form.programme_type,
      study_mode: form.study_mode,
      tuition: form.tuition,
      topics: form.topics,
      language: form.language,
      scholarship_required: form.scholarship_required,
      open_for_applications: form.open_for_applications,
      locale: locale.value,
      free_text: richParts.join('. '),
      cf_turnstile_response,
    }
    if (refineForm.career_goals.trim()) payload.career_goals = refineForm.career_goals.trim()
    if (refineForm.academic_background.trim())
      payload.academic_background = refineForm.academic_background.trim()
    const nationality = applicantStore.selectedApplicant?.nationalities[0]
    if (nationality) payload.country_of_origin = nationality

    const { data } = await axios.post<MatchResponse>(MATCHER_URL, payload)
    results.value = data.results
    hint.value = data.hint ?? null
    fallbackResults.value = data.fallback_results ?? []
    sessionId.value = data.session_id ?? null
    refining.value = false
    if (data.results.length > 0) void fetchTrending()
    saveSession()
  } catch (e) {
    console.error('Refine error:', e)
    refineError.value = true
  } finally {
    loading.value = false
  }
}

// ── Session persistence ───────────────────────────────────────────────
const SESSION_KEY = 'aithena_matcher_session'
const SESSION_TTL = 48 * 60 * 60 * 1000 // 48 hours
const isClient = typeof window !== 'undefined'

function _timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  return `${Math.floor(hours / 24)} day${Math.floor(hours / 24) === 1 ? '' : 's'} ago`
}

function saveSession() {
  if (!isClient) return
  try {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        form: { ...form },
        results: results.value,
        hint: hint.value,
        fallbackResults: fallbackResults.value,
        feedback: { ...feedback },
        trending: trending.value,
        savedAt: new Date().toISOString(),
      })
    )
  } catch {
    /* localStorage full or disabled */
  }
}

function loadSession() {
  if (!isClient) return
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return
    const data = JSON.parse(raw)
    if (!data.savedAt || Date.now() - new Date(data.savedAt).getTime() > SESSION_TTL) {
      localStorage.removeItem(SESSION_KEY)
      return
    }
    if (!data.results?.length && !data.fallbackResults?.length) return
    pendingSession.value = data as MatcherSession
    sessionAge.value = _timeAgo(data.savedAt)
    resumed.value = true
  } catch {
    localStorage.removeItem(SESSION_KEY)
  }
}

function restoreSession() {
  const data = pendingSession.value
  if (!data) return
  Object.assign(form, data.form ?? {})
  results.value = data.results ?? []
  hint.value = data.hint ?? null
  fallbackResults.value = data.fallbackResults ?? []
  Object.assign(feedback, data.feedback ?? {})
  trending.value = data.trending ?? []
  done.value = true
  resumed.value = false
  pendingSession.value = null
}

function discardSession() {
  if (!isClient) return
  localStorage.removeItem(SESSION_KEY)
  pendingSession.value = null
  resumed.value = false
}

const sessionSummary = computed<string[]>(() => {
  const f = pendingSession.value?.form
  if (!f) return []
  const chips: string[] = []
  if (f.programme_type && f.programme_type !== 'any') chips.push(f.programme_type)
  if (f.topics?.length) f.topics.slice(0, 3).forEach((t: string) => chips.push(t))
  if (f.language && f.language !== 'any') chips.push(f.language)
  if (f.tuition && f.tuition !== 'any')
    chips.push(
      ({ free: 'Free', low: 'Low cost', mid: 'Mid-range' } as Record<string, string>)[f.tuition] ??
        f.tuition
    )
  return chips
})

const selectionCrumbs = computed<string[]>(() => {
  const crumbs: string[] = []
  if (step.value > 0 && form.programme_type !== 'any')
    crumbs.push(
      typeOpts.value.find((o) => o.value === form.programme_type)?.label ?? form.programme_type
    )
  if (step.value > 1 && form.topics.length > 0) {
    form.topics
      .slice(0, 2)
      .forEach((v) => crumbs.push(topicOpts.value.find((o) => o.value === v)?.label ?? v))
    if (form.topics.length > 2) crumbs.push(`+${form.topics.length - 2}`)
  }
  if (step.value > 2 && form.study_mode !== 'any')
    crumbs.push(modeOpts.value.find((o) => o.value === form.study_mode)?.label ?? form.study_mode)
  if (step.value > 3 && form.language !== 'any')
    crumbs.push(languageOpts.value.find((o) => o.value === form.language)?.label ?? form.language)
  if (step.value > 4 && form.tuition !== 'any')
    crumbs.push(tuitionOpts.value.find((o) => o.value === form.tuition)?.label ?? form.tuition)
  return crumbs
})

watch(open, (isOpen) => {
  if (isOpen) loadSession()
})

// ── Click tracking ────────────────────────────────────────────────────
const trackClick = async (prog: ProgramMatch, rankPosition?: number) => {
  const cf_turnstile_response = await getTurnstileToken()
  axios
    .post(CLICK_URL, {
      programme_id: prog.programme_id,
      name_en: prog.name_en,
      session_id: sessionId.value ?? undefined,
      rank_position: rankPosition ?? undefined,
      cf_turnstile_response,
    })
    .catch(() => {})
}

// ── Feedback ──────────────────────────────────────────────────────────
const sendFeedback = async (programmeId: number, action: 'like' | 'dislike') => {
  if (feedback[programmeId] === action) return
  feedback[programmeId] = action
  try {
    const cf_turnstile_response = await getTurnstileToken()
    await axios.post(FEEDBACK_URL, { programme_id: programmeId, action, cf_turnstile_response })
  } catch (e) {
    console.error('Feedback error:', e)
  }
}

const toggleCompare = (programmeId: number) => {
  const idx = compareList.value.indexOf(programmeId)
  if (idx !== -1) {
    compareList.value.splice(idx, 1)
  } else if (compareList.value.length < 2) {
    compareList.value.push(programmeId)
  }
}

const runCompare = async () => {
  if (compareList.value.length !== 2) return
  compareProgs.value = compareList.value
    .map((id) => results.value.find((r) => r.programme_id === id))
    .filter(Boolean) as ProgramMatch[]
  compareLoading.value = true
  compareResult.value = null
  compareDialog.value = true
  try {
    const cf_turnstile_response = await getTurnstileToken()
    const { data } = await axios.post(COMPARE_URL, {
      programme_id_a: compareList.value[0],
      programme_id_b: compareList.value[1],
      locale: locale.value,
      cf_turnstile_response,
    })
    compareResult.value = data.comparison
  } catch {
    compareResult.value = 'Could not generate comparison. Please try again.'
  } finally {
    compareLoading.value = false
  }
}

const fetchTrending = async () => {
  if (trending.value.length > 0) return
  try {
    const { data } = await axios.get(TRENDING_URL)
    trending.value = data.programmes ?? []
  } catch {
    // non-critical, ignore
  }
}

const reset = () => {
  discardSession()
  step.value = 0
  done.value = false
  results.value = []
  hint.value = null
  fallbackResults.value = []
  sessionId.value = null
  compareList.value = []
  compareResult.value = null
  trending.value = []
  Object.keys(feedback).forEach((k) => delete (feedback as Record<string, unknown>)[k])
  refining.value = false
  refineError.value = false
  matchError.value = null
  Object.assign(refineForm, { career_goals: '', academic_background: '', extra: '' })
  Object.assign(form, {
    programme_type: 'any',
    topics: [],
    study_mode: 'any',
    language: 'any',
    tuition: 'any',
    scholarship_required: false,
    open_for_applications: false,
    free_text: '',
  })
}

function translateType(type: string): string {
  const map: Record<string, string> = {
    Master: t('program_matcher.results.badges.master'),
    Bachelor: t('program_matcher.results.badges.bachelor'),
    PhD: t('program_matcher.results.badges.phd'),
  }
  return map[type] ?? type
}

function translateMode(mode: string): string {
  const map: Record<string, string> = {
    'In Person': t('program_matcher.results.badges.in_person'),
    Remotely: t('program_matcher.results.badges.remotely'),
  }
  return map[mode] ?? mode
}

function getMatchLabel(score: number): string {
  if (score >= 0.52) return t('program_matcher.match_labels.excellent')
  if (score >= 0.44) return t('program_matcher.match_labels.great')
  if (score >= 0.36) return t('program_matcher.match_labels.good')
  if (score >= 0.28) return t('program_matcher.match_labels.fair')
  return t('program_matcher.match_labels.possible')
}

function getMatchClass(score: number): string {
  if (score >= 0.52) return 'score-pill--excellent'
  if (score >= 0.44) return 'score-pill--great'
  if (score >= 0.36) return 'score-pill--good'
  return 'score-pill--fair'
}

function getFallbackReasoning(prog: ProgramMatch): string {
  const el = locale.value.startsWith('el')
  const parts: string[] = []

  if (prog.programme_type && prog.programme_type !== 'any')
    parts.push(el ? `Αυτό το πρόγραμμα ${prog.programme_type}` : `This ${prog.programme_type} programme`)
  else
    parts.push(el ? 'Αυτό το πρόγραμμα' : 'This programme')

  if (prog.topics?.length)
    parts.push(el
      ? `καλύπτει ${prog.topics.slice(0, 2).join(' και ')}`
      : `covers ${prog.topics.slice(0, 2).join(' and ')}`)

  if (!prog.has_tuition_fees)
    parts.push(el ? 'χωρίς δίδακτρα' : 'with no tuition fees')
  else if (prog.tuition_fees_from)
    parts.push(el
      ? `από €${prog.tuition_fees_from.toLocaleString()}`
      : `starting from €${prog.tuition_fees_from.toLocaleString()}`)

  if (prog.study_modes?.length)
    parts.push(el
      ? `που προσφέρεται ${prog.study_modes[0] === 'Remotely' ? 'εξ αποστάσεως' : 'δια ζώσης'}`
      : `offered ${prog.study_modes[0].toLowerCase()}`)

  parts.push(el
    ? `στο ${prog.university_name} είναι ισχυρή αντιστοιχία για το προφίλ σου.`
    : `at ${prog.university_name} is a strong match for your profile.`)

  return parts.join(' ')
}
</script>

<style scoped lang="scss">
// ── Floating button ───────────────────────────────────────────
.matcher-fab {
  position: fixed;
  bottom: 28px;
  right: 24px;
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 22px 0 16px;
  background: linear-gradient(135deg, #fed230 0%, #f5c400 100%);
  color: $primary;
  border: none;
  border-radius: 32px;
  cursor: pointer;
  font-family: inherit;
  overflow: hidden;
  box-shadow:
    0 4px 20px rgba($accent, 0.55),
    0 8px 32px rgba(0, 0, 0, 0.18);
  animation:
    fab-float 3s ease-in-out infinite,
    fab-pulse 2.8s ease-out infinite;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  // shimmer sweep
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 40%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255, 255, 255, 0.45) 50%,
      transparent 100%
    );
    animation: fab-shimmer 2.6s ease-in-out infinite;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-3px) scale(1.04);
    box-shadow:
      0 6px 28px rgba($accent, 0.7),
      0 12px 40px rgba(0, 0, 0, 0.22);
    animation-play-state: paused;
  }
  &:active {
    transform: scale(0.97);
  }

  @media (max-width: 599px) {
    bottom: 20px;
    right: 16px;
    height: 50px;
    padding: 0 18px 0 14px;
    gap: 8px;
  }
}

.fab-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: $primary;
  flex-shrink: 0;

  @media (max-width: 599px) {
    width: 28px;
    height: 28px;
    border-radius: 8px;
  }
}

.fab-icon {
  color: $accent;
  animation: fab-spin 4s linear infinite;
}

.fab-text {
  font-size: 15px;
  font-weight: 800;
  color: $primary;
  white-space: nowrap;
  letter-spacing: 0.01em;

  @media (max-width: 599px) {
    font-size: 14px;
  }
}

.fab-arrow {
  color: $primary;
  opacity: 0.75;
  flex-shrink: 0;
}

@keyframes fab-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

@keyframes fab-pulse {
  0% {
    box-shadow:
      0 4px 20px rgba($accent, 0.55),
      0 8px 32px rgba(0, 0, 0, 0.18),
      0 0 0 0 rgba($accent, 0.7);
  }
  65% {
    box-shadow:
      0 4px 20px rgba($accent, 0.55),
      0 8px 32px rgba(0, 0, 0, 0.18),
      0 0 0 18px rgba($accent, 0);
  }
  100% {
    box-shadow:
      0 4px 20px rgba($accent, 0.55),
      0 8px 32px rgba(0, 0, 0, 0.18),
      0 0 0 0 rgba($accent, 0);
  }
}

@keyframes fab-shimmer {
  0% {
    left: -60%;
  }
  60% {
    left: 120%;
  }
  100% {
    left: 120%;
  }
}

@keyframes fab-spin {
  0% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(20deg);
  }
  75% {
    transform: rotate(-20deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

// ── Dialog card ───────────────────────────────────────────────
.font-poppins,
.font-poppins *:not(.q-icon):not(.material-icons) {
  font-family: 'Poppins', sans-serif !important;
}
.font-manrope,
.font-manrope *:not(.q-icon):not(.material-icons) {
  font-family: 'Manrope', sans-serif !important;
}

.matcher-card {
  width: 100%;
  max-width: 920px;
  overflow: hidden;
  border-radius: 20px !important;

  @media (max-width: 599px) {
    max-width: 98vw;
    border-radius: 16px !important;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
  }
}

// ── Header ────────────────────────────────────────────────────
.matcher-header {
  background: $primary;
  padding: 20px 22px 18px;

  @media (max-width: 599px) {
    padding: 14px 16px 12px;
  }
}

.matcher-title {
  font-size: 20px;
  line-height: 1.2;

  @media (max-width: 599px) {
    font-size: 18px;
  }
}

.aithena-title-sub {
  font-size: 14px;
  font-weight: 400;
  opacity: 0.7;

  @media (max-width: 599px) {
    display: none;
  }
}

.header-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: $accent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 599px) {
    width: 38px;
    height: 38px;
    border-radius: 10px;
  }
}

.step-progress-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.step-progress-fill {
  height: 100%;
  background: $accent;
  border-radius: 2px;
  transition: width 0.35s ease;
}

.step-dot {
  height: 6px;
  width: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  transition:
    background 0.2s,
    width 0.2s;
  &.active {
    background: $accent;
    width: 18px;
    border-radius: 3px;
  }
  &.done {
    background: rgba(255, 255, 255, 0.6);
  }
}

// ── Step form ─────────────────────────────────────────────────
.step-section {
  padding: 24px 24px 12px;

  @media (max-width: 599px) {
    padding: 18px 16px 10px;
    overflow-y: auto;
    flex: 1;
  }
}

.step-q {
  font-size: 18px;
  font-weight: 700;
  color: $primary;
  margin: 0 0 16px;
  line-height: 1.4;

  @media (max-width: 599px) {
    font-size: 17px;
    margin-bottom: 14px;
  }
}
.step-hint {
  font-size: 13px;
  font-weight: 400;
  color: #999;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-chip {
  padding: 10px 20px;
  border-radius: 24px;
  border: 2px solid $info;
  font-size: 15px;
  font-weight: 500;
  color: $primary;
  background: white;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  line-height: 1.2;

  &:hover {
    border-color: $primary;
    background: rgba($primary, 0.04);
  }
  &.selected {
    background: $primary;
    border-color: $primary;
    color: white;
  }

  @media (max-width: 599px) {
    padding: 9px 16px;
    font-size: 14px;
  }
}

.toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// ── Nav ───────────────────────────────────────────────────────
.nav-actions {
  padding: 12px 20px 20px;
  flex-shrink: 0;

  @media (max-width: 599px) {
    padding: 14px 16px 20px;
    flex-shrink: 0;
    gap: 10px;
  }
}

.nav-actions--last {
  @media (max-width: 599px) {
    flex-direction: column;
    align-items: center;

    .q-space { display: none; }

    .nav-btn {
      width: 100%;
      justify-content: center;
    }
  }
}

.nav-actions--refine {
  @media (max-width: 599px) {
    flex-direction: column;
    align-items: center;
    gap: 10px;

    .q-space { display: none; }
  }
}

.nav-btn {
  min-height: 44px;
  padding: 0 24px !important;
  font-size: 15px;

  @media (max-width: 599px) {
    min-height: 40px;
    padding: 0 14px !important;
    font-size: 13px;

    :deep(.q-icon) {
      font-size: 20px !important;
    }
  }
}

.nav-actions--refine .nav-btn {
  @media (max-width: 599px) {
    width: 100%;
  }
}

.nav-btn--primary {
  min-width: 150px;

  @media (max-width: 599px) {
    min-width: unset;
  }
}

// ── Results header ─────────────────────────────────────────────
.results-header {
  display: flex;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #f0f0f0;
  gap: 10px;

  @media (max-width: 599px) {
    padding: 12px 16px;
    gap: 8px;
    flex-wrap: wrap;
  }
}

.results-title-row {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;

  @media (max-width: 599px) {
    flex: 1 1 100%; // take full first line, forces btns to wrap below
  }
}

.results-header-btns {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 599px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-top: 10px;
  }
}

.results-action-btn {
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 15px;

  @media (max-width: 599px) {
    height: 38px !important;
    padding: 0 14px !important;
    font-size: 13px;
  }
}

.start-over-btn {
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 15px;

  @media (max-width: 599px) {
    padding: 0 14px !important;
    height: 38px !important;
    font-size: 13px;
    min-width: unset !important;
  }
}

.refine-cta {
  font-style: italic;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.refine-error {
  display: flex;
  align-items: center;
  margin: 0 20px 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fff3f3;
  color: #c62828;
  font-size: 14px;
}

// ── Result list ────────────────────────────────────────────────
.result-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 520px;
  overflow-y: auto;
  padding: 18px 22px 24px;
  scrollbar-width: thin;
  scrollbar-color: $info transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: $info;
    border-radius: 4px;
  }

  @media (max-width: 1023px) {
    max-height: 62vh;
    padding: 14px 16px 20px;
    gap: 12px;
  }
}

.result-card {
  border-radius: 16px;
  border: 1px solid transparent;
  padding: 14px 16px;
  background: #fff;
  transition: all 0.22s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 4px 14px rgba(0, 0, 0, 0.06);

  &:hover {
    border-color: rgba($primary, 0.12);
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.05),
      0 14px 36px rgba($primary, 0.12);
    transform: translateY(-2px);
  }

  @media (max-width: 599px) {
    padding: 14px 14px;
    border-radius: 14px;
  }
}

.result-inner {
  display: flex;
  gap: 14px;
  align-items: center;

  @media (max-width: 1023px) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }
}

.result-logo-wrap {
  flex-shrink: 0;
  position: relative;
  width: 120px;
  height: 120px;

  @media (max-width: 1023px) {
    width: 100%;
    height: 100px;
  }
}

.result-logo {
  width: 120px;
  height: 120px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 10px;

  @media (max-width: 1023px) {
    width: 100%;
    height: 100px;
    border-radius: 10px;
    padding: 16px 12px;
  }
}

.result-logo-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 12px;
  background: #f5f6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: 1023px) {
    width: 100%;
    height: 100px;
  }
}

.result-body {
  flex: 1;
  min-width: 0;
}

.result-top-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  @media (max-width: 1023px) {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 6px;
  }
}

.result-title {
  flex: 1;
  font-size: 17px;
  font-weight: 700;
  color: $primary;
  margin: 0;
  line-height: 1.35;

}

.result-meta {
  font-size: 15px;
  color: #757575;
  margin: 0 0 10px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 1023px) {
    white-space: normal;
  }
}

.badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.badge-item {
  font-size: 13px;
  padding: 5px 12px;
}

.lang-flag {
  height: 24px;
  width: 28px;
  border-radius: 3px;
  object-fit: cover;
  align-self: center;
  flex-shrink: 0;
}



.result-reasoning {
  font-size: 14px;
  color: #555;
  font-style: italic;
  margin: 0 0 12px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.aithena-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: $primary;
  color: white;
  font-size: 12px;
  font-weight: 700;
  font-style: normal;
  padding: 3px 9px 3px 7px;
  border-radius: 10px;
  flex-shrink: 0;
  letter-spacing: 0.03em;
  white-space: nowrap;
  margin-top: 1px;
}

.result-view-btn {
  margin-top: 6px;
  padding: 8px 20px !important;

  @media (max-width: 1023px) {
    width: 100%;
    font-size: 12px !important;
    padding: 10px 20px !important;
  }
}

// Score pill base
.score-pill {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.5;

  &.score-pill--excellent {
    background: linear-gradient(135deg, lighten($accent, 6%) 0%, $accent 100%);
    color: $primary;
    box-shadow: 0 2px 6px rgba($accent, 0.45);
  }
  &.score-pill--great {
    background: #e8f5e9;
    color: #2e7d32;
    box-shadow: 0 2px 6px rgba(46, 125, 50, 0.2);
  }
  &.score-pill--good {
    background: #e3f2fd;
    color: #1565c0;
    box-shadow: 0 2px 6px rgba(21, 101, 192, 0.18);
  }
  &.score-pill--fair {
    background: #f5f5f5;
    color: #616161;
    box-shadow: none;
  }
}

// ── Option chip static (non-interactive, for session summary) ─
.option-chip--static {
  pointer-events: none;
  background: lighten($primary, 52%) !important;
  border-color: lighten($primary, 40%) !important;
  color: $primary !important;
  cursor: default;
}

// ── New feature styles ────────────────────────────────────────

.card-actions-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.feedback-row {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-left: auto;
}

.feedback-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  color: #9e9e9e;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    border-color: $primary;
    color: $primary;
  }
  &--active {
    background: #f5f5f5;
    border-color: transparent;
  }
  &--like {
    color: #2e7d32 !important;
    border-color: #c8e6c9 !important;
    background: #f1f8e9 !important;
  }
  &--dislike {
    color: #c62828 !important;
    border-color: #ffcdd2 !important;
    background: #fff3f3 !important;
  }
}

.compare-toggle {
  font-size: 13px;
  font-weight: 600;
  padding: 5px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  background: white;
  cursor: pointer;
  color: #616161;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: $primary;
    color: $primary;
    background: #f0f4ff;
  }
  &--active {
    border-color: $primary !important;
    color: $primary !important;
    background: #e8eeff !important;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.compare-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  background: #f0f4ff;
  border: 1px solid lighten($primary, 40%);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  position: sticky;
  bottom: 0;
  margin-top: 8px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}

.fallback-hint-text {
  font-size: 14px;
  font-weight: 500;
  color: #3a3a3a;
  border-left: 3px solid $primary;
  padding: 4px 0 4px 12px;
  margin: 0 0 20px;
  line-height: 1.55;
}

.trending-section {
  padding: 8px 16px 12px;
  border-top: 1px solid #f5f5f5;
}

.trending-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9e9e9e;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.trending-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.trending-chip {
  font-size: 11px;
  color: #616161;
  background: #f5f5f5;
  border-radius: 20px;
  padding: 3px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}

// ── Compare dialog ────────────────────────────────────────────
.compare-dialog-card {
  min-width: 360px;
  max-width: 680px;
  width: 100%;
  border-radius: 18px !important;
  overflow: hidden;

  @media (max-width: 599px) {
    min-width: unset;
    width: 100%;
    max-height: 90vh;
    overflow-x: hidden;
    overflow-y: auto !important;
  }
}

.compare-dialog-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 15px 18px 12px;
  border-bottom: 1px solid #f0f0f0;
}

// ── Comparison layout ─────────────────────────────────────────
.cmp-wrapper {
  margin: 16px 18px 0;
  border: 1px solid #e8eaf6;
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 599px) {
    margin-left: 10px;
    margin-right: 10px;
    margin-top: 12px;
  }
}

.cmp-headers {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.cmp-col-header {
  background: $primary;
  color: white;
  font-size: 13px;
  font-weight: 700;
  padding: 14px 16px;
  line-height: 1.35;

  @media (max-width: 599px) {
    font-size: 12px;
    padding: 10px 10px;
  }
}

.cmp-col-header--b {
  border-left: 1px solid rgba(255, 255, 255, 0.15);
}

.cmp-section {
  border-top: 1px solid #e8eaf6;

  &:first-child {
    border-top: none;
  }
}

.cmp-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px 8px;
  font-size: 11px;

  @media (max-width: 599px) {
    padding: 8px 10px;
  }
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: $primary;
  background: #f5f6ff;
  border-bottom: 1px solid #e8eaf6;
}

.cmp-section-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.cmp-cell {
  background: white;
  font-size: 14px;
  color: #1a1a2e;
  line-height: 1.6;
  padding: 14px 16px;

  @media (max-width: 599px) {
    font-size: 12px;
    padding: 10px 10px;
  }
}

.cmp-cell--b {
  border-left: 1px solid #e8eaf6;
}

.cmp-verdict {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 12px 18px 16px;
  padding: 12px 16px;

  @media (max-width: 599px) {
    margin-left: 10px;
    margin-right: 10px;
  }
  background: #fffde7;
  border-left: 3px solid #ffb300;
  border-radius: 0 8px 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: #3e2c00;
  line-height: 1.55;
}

// ── Selection breadcrumbs (empty state) ──────────────────────
.empty-state-crumbs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.empty-crumb {
  font-size: 13px;
  font-weight: 600;
  color: #9e9e9e;
  white-space: nowrap;
}

.empty-crumb-sep {
  font-size: 20px;
  color: #d0d0d0;
  line-height: 1;
}

// ── Selection breadcrumbs (shown in header during wizard) ─────
.step-crumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.step-crumb {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
}

.step-crumb-sep {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1;
}

// ── Free text step ─────────────────────────────────────────────
.free-text-boost {
  display: flex;
  align-items: center;
  gap: 8px;
  border-left: 3px solid $primary;
  padding: 8px 14px;
  font-size: 15px;
  font-weight: 500;
  color: #555;
}

.free-text-boost-icon {
  color: $primary;
  flex-shrink: 0;

  @media (max-width: 599px) {
    font-size: 22px !important;
  }
}

.free-text-tip-icon {
  @media (max-width: 599px) {
    font-size: 20px !important;
  }
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.suggestion-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 13px;
  border-radius: 20px;
  border: 1.5px dashed rgba($primary, 0.3);
  font-size: 13px;
  font-weight: 500;
  color: $primary;
  background: white;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;

  &:hover {
    border-color: $primary;
    border-style: solid;
    background: rgba($primary, 0.05);
  }

  @media (max-width: 599px) {
    font-size: 12px;
    padding: 5px 11px;
  }
}

.free-text-tip {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #9e9e9e;
}

// ── 2K+ screens (≥ 2560px) ────────────────────────────────────
@media (min-width: 2560px) {
  .result-list {
    max-height: 560px;
    gap: 14px;
    padding: 16px 22px 22px;
  }

  .result-card {
    padding: 16px 18px;
  }

  .result-logo-wrap {
    width: 148px;
    height: 148px;
  }

  .result-logo {
    width: 148px;
    height: 148px;
  }

  .result-logo-placeholder {
    width: 148px;
    height: 148px;
  }

  .result-title {
    font-size: 18px;
  }

  .result-reasoning {
    font-size: 15px;
    line-height: 1.55;
  }
}
</style>

<style>
.matcher-dialog .q-dialog__backdrop {
  background: rgba(0, 0, 0, 0.75);
}

@media (max-width: 599px) {
  .compare-dialog .q-dialog__inner--minimized {
    padding: 0 !important;
  }
}
</style>
